import { useEffect, useRef, useState, useCallback } from 'react';
import './App.css';
import ChatAdvisor, { clearTemplateChatHistory } from './ChatAdvisor';
import BoardIframe from './BoardIframe';
import CodePreview from './CodePreview';
import useRole from './useRole';

// Auth0 is optional — only imported when the package is present
let useAuth0Impl = () => ({
  isAuthenticated: true, isLoading: false,
  user: null, getAccessTokenSilently: async () => null, logout: () => {},
  loginWithRedirect: () => {},
});
try {
  const auth0Module = require('@auth0/auth0-react');
  if (process.env.REACT_APP_AUTH0_DOMAIN && process.env.REACT_APP_AUTH0_CLIENT_ID) {
    useAuth0Impl = auth0Module.useAuth0;
  }
} catch (_) {}

function App() {
  // ── Auth0 ──────────────────────────────────────────────────────────────────
  const {
    isAuthenticated,
    isLoading: authLoading,
    user,
    getAccessTokenSilently,
    logout,
    loginWithRedirect,
  } = useAuth0Impl();

  const { isAdmin: jwtAdmin, isCreator: jwtCreator, isUser: jwtUser } = useRole();

  // Effective roles fetched from backend (respects local overrides set by admin panel)
  const [effectiveRoles, setEffectiveRoles] = useState(null);
  const [assignedApps, setAssignedApps] = useState(null); // null = all (admin)
  const isAdmin    = effectiveRoles ? effectiveRoles.includes('admin')   : jwtAdmin;
  const isCreator  = effectiveRoles ? effectiveRoles.includes('creator') : jwtCreator;
  const isUser     = effectiveRoles ? (effectiveRoles.includes('user') && !effectiveRoles.includes('admin') && !effectiveRoles.includes('creator')) : jwtUser;

  // Returns { Authorization: 'Bearer ...' } when Auth0 is active, else {}
  const getAuthHeaders = useCallback(async () => {
    if (!isAuthenticated || !process.env.REACT_APP_AUTH0_AUDIENCE) return {};
    try {
      const token = await getAccessTokenSilently({
        authorizationParams: { audience: process.env.REACT_APP_AUTH0_AUDIENCE },
      });
      return { Authorization: `Bearer ${token}` };
    } catch (e) {
      console.error('[Auth] getAccessTokenSilently failed:', e);
      return {};
    }
  }, [isAuthenticated, getAccessTokenSilently]);

  // ─────────────────────────────────────────────────────────────────────────
  const iframeRef = useRef(null);
  const boardFrameRef = useRef(null);
  const loadingTimeoutRef = useRef(null);
  const [saveStatus, setSaveStatus] = useState('');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isBoardLoading, setIsBoardLoading] = useState(true);
  const [boardError, setBoardError] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState('template-1');
  const [availableTemplates, setAvailableTemplates] = useState([]);
  const [showAddTemplateModal, setShowAddTemplateModal] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState('');
  const [newTemplateDescription, setNewTemplateDescription] = useState('');
  const [newTemplateImage, setNewTemplateImage] = useState(null);
  const [newTemplateImagePreview, setNewTemplateImagePreview] = useState(null);
  const [newTemplateFile, setNewTemplateFile] = useState(null);
  const [newTemplateFilePath, setNewTemplateFilePath] = useState(null);
  const [newTemplateModel, setNewTemplateModel] = useState('openai'); // Model selection for template creation
  const [crossAppEnabled, setCrossAppEnabled] = useState(false);
  const [crossAppReadFrom, setCrossAppReadFrom] = useState([]); // array of app UUIDs
  const [crossAppWriteTo, setCrossAppWriteTo] = useState([]);   // array of app UUIDs
  const [isCreatingTemplate, setIsCreatingTemplate] = useState(false);
  const [creatingTemplateName, setCreatingTemplateName] = useState('');
  const templateImageInputRef = useRef(null);
  const templateFileInputRef = useRef(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null);
  const [deleteStep, setDeleteStep] = useState(1); // 1 = first confirmation, 2 = second confirmation
  const [showGallery, setShowGallery] = useState(false);
  const [galleryViewMode, setGalleryViewMode] = useState(true); // Default to gallery view on load
  const [chatDocked, setChatDocked] = useState(true);
  const [chatCollapsed, setChatCollapsed] = useState(false);
  const [chatMinimized, setChatMinimized] = useState(true);
  const [chatNudge, setChatNudge] = useState(false);
  const [chatTooltip, setChatTooltip] = useState(false);
  const [galleryNudge, setGalleryNudge] = useState(false);
  const [galleryTooltip, setGalleryTooltip] = useState(false);
  const [chatPosition, setChatPosition] = useState({ x: 720, y: 140 });
  const chatDragRef = useRef({ dragging: false, offsetX: 0, offsetY: 0 });
  const prevGalleryModeRef = useRef(galleryViewMode);
  const chatMountRef = useRef(false);
  const chatEffectRunsRef = useRef(0);
  const API_URL = (process.env.REACT_APP_API_URL ?? '').trim().replace(/\/+$/, '');
  const IFRAME_URL = `${API_URL}/board.html`;

  // Derive allowed origins dynamically from environment or current location
  const getAllowedOrigins = () => {
    const origins = new Set();
    try {
      if (API_URL) origins.add(new URL(API_URL).origin);
    } catch (e) {}
    try {
      const envBack = (process.env.REACT_APP_BACKEND_URL ?? '').trim();
      if (envBack) origins.add(new URL(envBack).origin);
    } catch (e) {}
    const extraOrigins = (process.env.REACT_APP_ALLOWED_ORIGINS ?? '')
      .split(',')
      .map(value => value.trim())
      .filter(Boolean);
    extraOrigins.forEach(origin => {
      try {
        origins.add(new URL(origin).origin);
      } catch (_) {}
    });
    origins.add(window.location.origin);
    return Array.from(origins);
  };

  // Generate or retrieve session ID for ownership tracking
  const getSessionId = () => {
    // When authenticated via Auth0, use the stable user sub as the owner ID
    if (isAuthenticated && user?.sub) return user.sub;
    let sessionId = localStorage.getItem('app_session_id');
    if (!sessionId) {
      sessionId = `session_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
      localStorage.setItem('app_session_id', sessionId);
    }
    return sessionId;
  };

  const [templateOwnership, setTemplateOwnership] = useState({});
  const [showCodePreview, setShowCodePreview] = useState(false);
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [copyAppName, setCopyAppName] = useState('');
  const [isCopying, setIsCopying] = useState(false);
  const [showDeployConfirmModal, setShowDeployConfirmModal] = useState(false);
  const [showDeployModal, setShowDeployModal] = useState(false);
  const [isDeploying, setIsDeploying] = useState(false);
  const [deploymentStatus, setDeploymentStatus] = useState(null);
  const [deploymentUrl, setDeploymentUrl] = useState(null);
  const [existingDeployment, setExistingDeployment] = useState(null);

  // ── Admin Panel ──────────────────────────────────────────────────────────────
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [adminTab, setAdminTab] = useState('users'); // 'users' | 'access'
  const [adminUsers, setAdminUsers] = useState([]);
  const [adminUsersLoading, setAdminUsersLoading] = useState(false);
  const [adminUserApps, setAdminUserApps] = useState({}); // { user_id: [app_id, ...] }
  const [adminSavingUser, setAdminSavingUser] = useState(null);
  const [adminSelectedBoard, setAdminSelectedBoard] = useState(null);
  const [adminRoleEdits, setAdminRoleEdits] = useState({}); // { user_id: 'admin'|'creator'|'user' }
  const [adminSavingRole, setAdminSavingRole] = useState(null);

  // ── Version History ─────────────────────────────────────────────────────────
  const [showVersionModal, setShowVersionModal] = useState(false);
  const [versions, setVersions] = useState([]);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [revertingVersion, setRevertingVersion] = useState(null);
  const [quickUndoLoading, setQuickUndoLoading] = useState(false);

  // Open-dev mode (no Auth0): load all templates without filtering
  useEffect(() => {
    if (process.env.REACT_APP_AUTH0_DOMAIN) return; // Auth0 mode handled below
    fetch(`${API_URL}/board/templates`)
      .then(r => r.json())
      .then(d => {
        if (d.templates?.length > 0) {
          setAvailableTemplates(d.templates);
          setSelectedTemplate(prev => d.templates.find(t => t.id === prev) ? prev : d.templates[0].id);
        }
      })
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [API_URL]);

  // Register user + load their assigned templates (single auth-gated fetch)
  useEffect(() => {
    if (!isAuthenticated || !process.env.REACT_APP_AUTH0_DOMAIN || !user) return;
    (async () => {
      try {
        const authHeaders = await getAuthHeaders();
        if (!authHeaders.Authorization) return;

        // 1. Fetch effective roles + assigned apps
        const params = new URLSearchParams();
        if (user.email)   params.set('x_user_email',   user.email);
        if (user.name)    params.set('x_user_name',    user.name);
        if (user.picture) params.set('x_user_picture', user.picture);
        const meRes = await fetch(`${API_URL}/board/users/me?${params}`, { headers: authHeaders });
        if (meRes.ok) {
          const meData = await meRes.json();
          if (Array.isArray(meData.roles)) setEffectiveRoles(meData.roles);
          // null means all-access (admin), array means restricted list
          setAssignedApps(meData.apps === null ? null : (meData.apps || []));
        }

        // 2. Load templates filtered by role (auth header required for correct filtering)
        const tRes = await fetch(`${API_URL}/board/templates`, { headers: authHeaders });
        if (tRes.ok) {
          const tData = await tRes.json();
          if (tData.templates) {
            setAvailableTemplates(tData.templates);
            if (tData.templates.length > 0) {
              setSelectedTemplate(prev => {
                const exists = tData.templates.find(t => t.id === prev);
                return exists ? prev : tData.templates[0].id;
              });
            }
          }
        }
      } catch (_) {}
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user?.sub]);

  const fetchAdminUsers = async () => {
    setAdminUsersLoading(true);
    try {
      const authHeaders = await getAuthHeaders();
      const res = await fetch(`${API_URL}/admin/users`, { headers: authHeaders });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const users = data.users || [];
      setAdminUsers(users);
      const appsMap = {};
      users.forEach(u => { if (u.apps !== null) appsMap[u.user_id] = u.apps || []; });
      setAdminUserApps(appsMap);
    } catch (e) {
      console.error('fetchAdminUsers error', e);
    } finally {
      setAdminUsersLoading(false);
    }
  };

  const saveUserRole = async (userId) => {
    const role = adminRoleEdits[userId];
    if (!role) return;
    setAdminSavingRole(userId);
    try {
      const authHeaders = await getAuthHeaders();
      const res = await fetch(`${API_URL}/admin/users/${encodeURIComponent(userId)}/role`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({ roles: [role] }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      // Refresh the user list to reflect the new role
      await fetchAdminUsers();
    } catch (e) {
      console.error('saveUserRole error', e);
    } finally {
      setAdminSavingRole(null);
    }
  };

  const saveUserApps = async (userId) => {
    setAdminSavingUser(userId);
    try {
      const authHeaders = await getAuthHeaders();
      const res = await fetch(`${API_URL}/admin/users/${encodeURIComponent(userId)}/apps`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({ app_ids: adminUserApps[userId] || [] }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
    } catch (e) {
      console.error('saveUserApps error', e);
    } finally {
      setAdminSavingUser(null);
    }
  };

  const fetchVersions = async (templateId) => {
    setVersionsLoading(true);
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`${API_URL}/board/templates/${templateId}/versions?owner_id=${encodeURIComponent(getSessionId())}`, { headers });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setVersions(data.versions || []);
    } catch (e) {
      console.error('fetchVersions error', e);
      setVersions([]);
    } finally {
      setVersionsLoading(false);
    }
  };

  const handleOpenVersionModal = () => {
    setShowVersionModal(true);
    fetchVersions(selectedTemplate);
  };

  const handleRevert = async (versionId) => {
    setRevertingVersion(versionId);
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(
        `${API_URL}/board/templates/${selectedTemplate}/revert/${versionId}?owner_id=${encodeURIComponent(getSessionId())}`,
        { method: 'POST', headers },
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (data.success) {
        // Reload iframe first, then refresh version list so modal stays accurate
        if (iframeRef.current) {
          iframeRef.current.src = `${IFRAME_URL}?template=${selectedTemplate}&t=${Date.now()}`;
        }
        setSaveStatus('Reverted');
        setShowVersionModal(false);
        // Refresh version list in background so reopening modal shows fresh data
        fetchVersions(selectedTemplate);
      }
    } catch (e) {
      console.error('handleRevert error', e);
      alert('Failed to revert. Please try again.');
    } finally {
      setRevertingVersion(null);
    }
  };

  const handleQuickUndo = async () => {
    if (quickUndoLoading) return;
    setQuickUndoLoading(true);
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`${API_URL}/board/templates/${selectedTemplate}/versions?owner_id=${encodeURIComponent(getSessionId())}`, { headers });
      if (!res.ok) return;
      const data = await res.json();
      const list = data.versions || [];
      if (list.length === 0) {
        alert('No previous versions to restore.');
        return;
      }
      await handleRevert(list[0].version_id); // newest snapshot first
    } catch (e) {
      console.error('handleQuickUndo error', e);
    } finally {
      setQuickUndoLoading(false);
    }
  };

  // Check ownership for a template
  const checkTemplateOwnership = (templateId) => {
    if (!templateId) return false;

    // Admin has full access to every board
    if (isAdmin || assignedApps === null) {
      setTemplateOwnership(prev => ({
        ...prev,
        [templateId]: { is_owner: true, can_edit: true },
      }));
      return true;
    }

    // For all other roles: access is determined by assignment, not session ownership
    const hasAccess = Array.isArray(assignedApps) && assignedApps.includes(templateId);
    setTemplateOwnership(prev => ({
      ...prev,
      [templateId]: { is_owner: hasAccess, can_edit: hasAccess },
    }));
    return hasAccess;
  };

  useEffect(() => {
    document.title = 'ByteBOARD | AI digital whiteboards';
    // Intentional one-time setup for title
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [API_URL, isBoardLoading]);

  // Chat floating drag handlers
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!chatDragRef.current.dragging) return;
      setChatPosition({
        x: Math.max(8, chatDragRef.current.offsetX + e.clientX),
        y: Math.max(8, chatDragRef.current.offsetY + e.clientY),
      });
    };
    const handleMouseUp = () => {
      chatDragRef.current.dragging = false;
    };
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  // Nudge chat icon when minimized
  useEffect(() => {
    chatEffectRunsRef.current += 1;
    if (!chatMountRef.current || chatEffectRunsRef.current <= 2) {
      chatMountRef.current = true;
      return;
    }
    if (chatMinimized) {
      setChatNudge(true);
      setChatTooltip(true);
      const t1 = setTimeout(() => setChatNudge(false), 1800);
      const t2 = setTimeout(() => setChatTooltip(false), 2800);
      return () => {
        clearTimeout(t1);
        clearTimeout(t2);
      };
    }
    // clear any lingering tooltip when opened
    setChatNudge(false);
    setChatTooltip(false);
  }, [chatMinimized]);

  // Nudge gallery icon when entering edit mode (gallery -> board)
  useEffect(() => {
    if (prevGalleryModeRef.current === true && galleryViewMode === false) {
      setChatMinimized(false); // auto-open chat in edit mode
      setGalleryNudge(true);
      setGalleryTooltip(true);
      const t1 = setTimeout(() => setGalleryNudge(false), 1800);
      const t2 = setTimeout(() => setGalleryTooltip(false), 2800);
      return () => {
        clearTimeout(t1);
        clearTimeout(t2);
      };
    }
    prevGalleryModeRef.current = galleryViewMode;
  }, [galleryViewMode]);

  // Get state from iframe using postMessage (cross-origin safe)

  const handleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };


  // Re-evaluate access whenever template or assigned apps change
  useEffect(() => {
    if (selectedTemplate) {
      checkTemplateOwnership(selectedTemplate);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTemplate, assignedApps, isAdmin]);

  // Fetch existing deployment info when template changes
  useEffect(() => {
    const fetchDeploymentInfo = async () => {
      if (!selectedTemplate) return;
      try {
        const authHeaders = await getAuthHeaders();
        const response = await fetch(`${API_URL}/board/templates/${encodeURIComponent(selectedTemplate)}/deployment`, { headers: authHeaders });
        const data = await response.json();
        if (data.success && data.deployment) {
          setExistingDeployment(data.deployment);
        } else {
          setExistingDeployment(null);
        }
      } catch (error) {
        console.error('Error fetching deployment info:', error);
        setExistingDeployment(null);
      }
    };
    fetchDeploymentInfo();
  }, [API_URL, selectedTemplate]);

  // Handle ESC key to exit fullscreen
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isFullscreen) {
        setIsFullscreen(false);
      }
    };

    if (isFullscreen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isFullscreen]);

  // Handle iframe load events to show/hide loading indicator and catch errors
  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) {
      // If iframe ref is not ready, try again after a short delay
      const retryTimeout = setTimeout(() => {
        if (iframeRef.current) {
          setIsBoardLoading(false);
        }
      }, 1000);
      return () => clearTimeout(retryTimeout);
    }

    const handleLoad = () => {
      console.log('Iframe load event fired, hiding loading indicator');
      // Clear timeout if iframe loads successfully
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
        loadingTimeoutRef.current = null;
      }
      // Hide loading immediately when load event fires
      // postMessage is just a bonus confirmation, but load event is reliable
      setIsBoardLoading(false);
      setBoardError(null); // Clear any previous errors on successful load
    };

    const handleError = (event) => {
      console.error('Iframe load error:', event);
      setIsBoardLoading(false);
      setBoardError('Failed to load board. Please check if the backend server is running.');
    };

    // Set loading initially
    setIsBoardLoading(true);
    setBoardError(null);

    // Listen for load and error events
    iframe.addEventListener('load', handleLoad);
    iframe.addEventListener('error', handleError);

    // Also check if iframe is already loaded (with retry for cross-origin)
    const checkIfLoaded = () => {
      try {
        if (iframe.contentDocument && iframe.contentDocument.readyState === 'complete') {
          console.log('Iframe already loaded (same-origin)');
          handleLoad();
          return true;
        }
      } catch (e) {
        // Cross-origin, can't access directly - that's expected
        console.log('Cannot access iframe document (cross-origin - expected)');
        // For cross-origin, we rely on postMessage or load event
        // Check if iframe src has loaded by checking if it's not blank
        if (iframe.src && iframe.src !== 'about:blank') {
          // Give it a moment, then check if load event fires
          setTimeout(() => {
            // If still loading after 2 seconds, try to trigger load check
            if (isBoardLoading) {
              console.log('Iframe src set but no load event yet, waiting for postMessage...');
            }
          }, 2000);
        }
      }
      return false;
    };
    
    // Check immediately
    if (!checkIfLoaded()) {
      // Also check after a short delay
      setTimeout(checkIfLoaded, 500);
    }

    // Clear any existing timeout
    if (loadingTimeoutRef.current) {
      clearTimeout(loadingTimeoutRef.current);
    }
    
    // Also check for errors after a timeout
    loadingTimeoutRef.current = setTimeout(() => {
      // If still loading after 15 seconds, show error
      console.log('Loading timeout reached, checking iframe state');
      // Check if iframe actually loaded but message wasn't received
      if (iframeRef.current) {
        try {
          // Try to access iframe to see if it loaded
          const iframeDoc = iframeRef.current.contentDocument || iframeRef.current.contentWindow?.document;
          if (iframeDoc && iframeDoc.readyState === 'complete') {
            console.log('Iframe loaded but message not received, hiding loading');
            setIsBoardLoading(false);
            return;
          }
        } catch (e) {
          // Cross-origin, can't access - that's expected
          console.log('Cannot access iframe (cross-origin), checking network');
        }
      }
      
      // Check if backend is reachable (use public root endpoint — no auth needed)
      fetch(`${API_URL}/`)
        .then(() => {
          // Backend is reachable, iframe might just be slow
          setBoardError('Artifacts are taking longer than expected to load. The backend server is running. Please try refreshing or check the browser console for errors.');
        })
        .catch(() => {
          // Backend is not reachable
          setBoardError('Cannot connect to backend server. Please ensure the backend server is running on port 8000. Start it with: python backend/app/main.py');
        });
      
      setIsBoardLoading(false);
    }, 15000); // Increased to 15 seconds

    return () => {
      iframe.removeEventListener('load', handleLoad);
      iframe.removeEventListener('error', handleError);
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
      }
    };
  }, [API_URL]);

  // Listen for IFRAME_LOADED message from BoardIframe to hide loading
  useEffect(() => {
    const handleMessage = (event) => {
      console.log('Received postMessage:', event.data, 'from origin:', event.origin);
      
      // Accept messages from backend origin (iframe) or same origin
      const allowed = getAllowedOrigins();
      const isBackendOrigin = allowed.some(o => event.origin === o || event.origin.startsWith(o));
      
      if (!isBackendOrigin && event.origin !== '*') {
        console.log('Ignoring message from origin:', event.origin);
        return;
      }
      
      if (event.data && event.data.type === 'IFRAME_LOADED') {
        console.log('IFRAME_LOADED message received, hiding loading indicator');
        // Clear timeout if message received
        if (loadingTimeoutRef.current) {
          clearTimeout(loadingTimeoutRef.current);
          loadingTimeoutRef.current = null;
        }
        setIsBoardLoading(false);
        setBoardError(null); // Clear errors on successful load
      } else if (event.data && event.data.type === 'IFRAME_ERROR') {
        console.log('IFRAME_ERROR message received:', event.data.error);
        if (loadingTimeoutRef.current) {
          clearTimeout(loadingTimeoutRef.current);
          loadingTimeoutRef.current = null;
        }
        setIsBoardLoading(false);
        setBoardError(event.data.error || 'Failed to load artifacts content.');
      } else if (event.data && event.data.type === 'STATE_RESTORED') {
        // Also hide loading when state is restored (means iframe loaded)
        console.log('STATE_RESTORED message received, iframe is loaded');
        if (loadingTimeoutRef.current) {
          clearTimeout(loadingTimeoutRef.current);
          loadingTimeoutRef.current = null;
        }
        setIsBoardLoading(false);
        setBoardError(null);
      }
    };

    window.addEventListener('message', handleMessage);
    console.log('Added message listener for IFRAME_LOADED');
    
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  const handleDeleteClick = (templateId) => {
    // Prevent deletion of template-1
    if (templateId === 'template-1') {
      alert('Cannot delete Board 1 (default board)');
      return;
    }

    const template = availableTemplates.find(t => t.id === templateId);
    setTemplateToDelete({ id: templateId, name: template ? template.name : templateId });
    setDeleteStep(1);
    setShowDeleteModal(true);
  };

  const handleCopyApp = async () => {
    if (!copyAppName.trim()) {
      alert('Please enter a name for the copied board');
      return;
    }

    setIsCopying(true);
    try {
      const sessionId = getSessionId();
      const authHeaders = await getAuthHeaders();
      const response = await fetch(`${API_URL}/board/templates/${encodeURIComponent(selectedTemplate)}/copy`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders,
        },
        body: JSON.stringify({
          new_name: copyAppName.trim(),
          owner_id: sessionId
        }),
      });

      const data = await response.json();
      
      if (data.success) {
        // Reload templates list
        const templatesResponse = await fetch(`${API_URL}/board/templates`, { headers: await getAuthHeaders() });
        const templatesData = await templatesResponse.json();
        if (templatesData.templates && templatesData.templates.length > 0) {
          setAvailableTemplates(templatesData.templates);
        }
        
        // Switch to the new copied template
        setSelectedTemplate(data.template_id);
        if (iframeRef.current) {
          setIsBoardLoading(true);
          iframeRef.current.src = `${IFRAME_URL}?template=${data.template_id}`;
        }
        
        // Close modal and reset
        setShowCopyModal(false);
        setCopyAppName('');
        
        // Grant edit access locally for the copied board
        if (isCreator) {
          setAssignedApps(prev => prev === null ? null : [...(prev || []), data.template_id]);
        }
        checkTemplateOwnership(data.template_id);
      } else {
        alert(`Error copying board: ${data.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error copying template:', error);
      alert(`Error copying board: ${error.message}`);
    } finally {
      setIsCopying(false);
    }
  };

  const handleDeploy = async () => {
    if (!selectedTemplate) return;

    setIsDeploying(true);
    setDeploymentStatus(null);
    setDeploymentUrl(null);

    try {
      const authHeaders = await getAuthHeaders();
      const response = await fetch(`${API_URL}/board/templates/${encodeURIComponent(selectedTemplate)}/deploy?owner_id=${encodeURIComponent(getSessionId())}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
      });

      const data = await response.json();
      if (!response.ok) {
        setDeploymentStatus('error');
        setDeploymentUrl(null);
        const errorMsg = data.error || data.message || `HTTP ${response.status}: ${response.statusText}`;
        alert(`Deployment failed: ${errorMsg}`);
        return;
      }

      if (data.success) {
        const url = data.deployment_url || data.production_url || null;
        setDeploymentStatus('success');
        setDeploymentUrl(url);
        if (url) {
          setExistingDeployment({
            deployment_url: url,
            deployed_at: new Date().toISOString()
          });
        }
      } else {
        setDeploymentStatus('error');
        setDeploymentUrl(null);
        const errorMsg = data.error || data.message || 'Unknown error';
        alert(`Deployment failed: ${errorMsg}`);
      }
    } catch (error) {
      setDeploymentStatus('error');
      setDeploymentUrl(null);
      alert(`Error deploying app: ${error.message}`);
    } finally {
      setIsDeploying(false);
    }
  };

  const handleDeployClick = () => setShowDeployConfirmModal(true);

  const handleDeployConfirm = () => {
    setShowDeployConfirmModal(false);
    setShowDeployModal(true);
    handleDeploy();
  };

  const handleDeleteConfirm = async () => {
    if (!templateToDelete) return;

    if (deleteStep === 1) {
      // First confirmation - move to second step
      setDeleteStep(2);
      return;
    }

    // Second confirmation - proceed with deletion
    try {
      const sessionId = getSessionId();
      const authHeaders = await getAuthHeaders();
      const response = await fetch(`${API_URL}/board/templates/${encodeURIComponent(templateToDelete.id)}?owner_id=${encodeURIComponent(sessionId)}`, {
        method: 'DELETE',
        headers: { ...authHeaders },
      });

      const data = await response.json();
      
      if (data.success) {
        // Clear chat history for the deleted template BEFORE reloading templates
        console.log(`[App] Deleting board ${templateToDelete.id}, clearing chat history...`);
        clearTemplateChatHistory(templateToDelete.id, getAuthHeaders);
        
        // Reload templates list
        const templatesResponse = await fetch(`${API_URL}/board/templates`, { headers: await getAuthHeaders() });
        const templatesData = await templatesResponse.json();
        if (templatesData.templates && templatesData.templates.length > 0) {
          setAvailableTemplates(templatesData.templates);
          
          // If deleted template was selected, go back to gallery
          if (selectedTemplate === templateToDelete.id) {
            setGalleryViewMode(true);
          }
        }
        // Close modal and reset
        setShowDeleteModal(false);
        setTemplateToDelete(null);
        setDeleteStep(1);
      } else {
        alert(`Error deleting board: ${data.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error deleting template:', error);
      alert(`Error deleting board: ${error.message}`);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setTemplateToDelete(null);
    setDeleteStep(1);
  };

  const handleOpenTemplateFromGallery = (templateId) => {
    setGalleryViewMode(false);
    setShowGallery(false);
    setChatMinimized(false);
    // Switch to the template
    setSelectedTemplate(templateId);
    if (iframeRef.current) {
      setIsBoardLoading(true);
      iframeRef.current.src = `${IFRAME_URL}?template=${templateId}`;
    }
  };

  const handleTemplateImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        alert('Please select an image file');
        return;
      }
      
      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        alert('Image size must be less than 10MB');
        return;
      }
      
      setNewTemplateImage(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setNewTemplateImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeTemplateImage = () => {
    setNewTemplateImage(null);
    setNewTemplateImagePreview(null);
    if (templateImageInputRef.current) {
      templateImageInputRef.current.value = '';
    }
  };

  const handleTemplateFileSelect = async (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const fileExt = file.name.toLowerCase().split('.').pop();
      if (!['csv', 'xlsx', 'xls'].includes(fileExt)) {
        alert('Please select a CSV or Excel file (.csv, .xlsx, .xls)');
        return;
      }
      
      // Validate file size (max 50MB for data files)
      if (file.size > 50 * 1024 * 1024) {
        alert('File size must be less than 50MB');
        return;
      }
      
      setNewTemplateFile(file);
      
      // Upload the file immediately
      try {
        const formData = new FormData();
        formData.append('file', file);
        
        const uploadAuthHeaders = await getAuthHeaders();
        const response = await fetch(`${API_URL}/upload`, {
          method: 'POST',
          headers: { ...uploadAuthHeaders },
          body: formData,
        });
        
        const data = await response.json();
        if (data.success) {
          setNewTemplateFilePath(data.file_path);
          console.log(`Template file uploaded: ${data.file_path}`);
        } else {
          alert(`Upload failed: ${data.error || 'Unknown error'}`);
          setNewTemplateFile(null);
        }
      } catch (error) {
        console.error('Error uploading template file:', error);
        alert('Failed to upload file. Please try again.');
        setNewTemplateFile(null);
      }
    }
  };

  const removeTemplateFile = () => {
    setNewTemplateFile(null);
    setNewTemplateFilePath(null);
    if (templateFileInputRef.current) {
      templateFileInputRef.current.value = '';
    }
  };

  const convertImageToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        // Remove data URL prefix (data:image/png;base64,)
        const base64 = reader.result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleCreateTemplate = async () => {
    if (!newTemplateName.trim() || !newTemplateDescription.trim()) {
      alert('Please provide both board name and description');
      return;
    }

    setIsCreatingTemplate(true);
    
    // Save form data before clearing
    const savedName = newTemplateName.trim();
    const savedDescription = newTemplateDescription.trim();
    const imageToSend = newTemplateImage;
    setCreatingTemplateName(savedName);
    
    // Optimistically add the template to the list (we'll update with real data later)
    const tempTemplateName = savedName;
    const estimatedNextNum = availableTemplates.length + 1;
    const tempTemplateId = `template-${estimatedNextNum}`;
    
    // Add temporary template to list
    const tempTemplate = { id: tempTemplateId, name: tempTemplateName };
    setAvailableTemplates([...availableTemplates, tempTemplate]);
    
    // Switch to the new template immediately with loading state
    setSelectedTemplate(tempTemplateId);
    setIsBoardLoading(true);
    setBoardError(null);
    
    // Close modal immediately for better UX
    setShowAddTemplateModal(false);
    setNewTemplateName('');
    setNewTemplateDescription('');
    setNewTemplateModel('openai'); // Reset to default model
    setCrossAppEnabled(false);
    setCrossAppReadFrom([]);
    setCrossAppWriteTo([]);
    
    // Prepare image data
    let imageBase64 = null;
    let imageMimeType = null;
    if (imageToSend) {
      imageBase64 = await convertImageToBase64(imageToSend);
      imageMimeType = imageToSend.type;
    }
    
    // Clear image and file state
    removeTemplateImage();
    removeTemplateFile();
    
    try {
      const sessionId = getSessionId();
      const requestBody = {
        name: savedName,
        description: savedDescription,
        model: newTemplateModel, // Use selected model
        owner_id: sessionId,
        cross_app_config: (crossAppEnabled && (crossAppReadFrom.length > 0 || crossAppWriteTo.length > 0))
          ? { can_read_from: crossAppReadFrom, can_write_to: crossAppWriteTo }
          : null,
      };
      
      if (imageBase64) {
        requestBody.image_base64 = imageBase64;
        requestBody.image_mime_type = imageMimeType;
      }
      
      if (newTemplateFilePath) {
        requestBody.uploaded_file_path = newTemplateFilePath;
      }
      
      const authHeaders = await getAuthHeaders();
      const response = await fetch(`${API_URL}/board/templates/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify(requestBody),
      });

      let data;
      try { data = await response.json(); } catch { data = {}; }

      // Normalise FastAPI validation/auth errors ({"detail": "..."}) into our format
      if (!data.success && !data.error && data.detail) {
        data.error = typeof data.detail === 'string'
          ? data.detail
          : JSON.stringify(data.detail);
      }
      if (!response.ok && !data.error) {
        data.error = `HTTP ${response.status} ${response.statusText}`;
      }

      if (data.success) {
        // Reload templates list to get the actual template data
        const templatesResponse = await fetch(`${API_URL}/board/templates`, { headers: await getAuthHeaders() });
        const templatesData = await templatesResponse.json();
        if (templatesData.templates && templatesData.templates.length > 0) {
          setAvailableTemplates(templatesData.templates);
          // Ensure we're on the correct template
          setSelectedTemplate(data.template_id);

          // Grant edit access locally — editor's newly created board is auto-assigned on backend
          if (isCreator) {
            setAssignedApps(prev => prev === null ? null : [...(prev || []), data.template_id]);
          }
          
          // Clear any existing chat history for this template AFTER switching
          // This ensures ChatAdvisor component sees the new template first
          setTimeout(() => {
            clearTemplateChatHistory(data.template_id, getAuthHeaders);
            // Force a page reload of ChatAdvisor by triggering a re-render
            // The ChatAdvisor will reload messages after the clear
            console.log(`[App] Cleared chat history for new board: ${data.template_id}`);
          }, 100);
          
          // Load the iframe with the new template
          if (iframeRef.current) {
            iframeRef.current.src = `${IFRAME_URL}?template=${data.template_id}`;
          }
        }
      } else {
        // Remove the temporary template on error
        setAvailableTemplates(prev => prev.filter(t => t.id !== tempTemplateId));
        // Switch back to template-1
        setSelectedTemplate('template-1');
        if (iframeRef.current) {
          iframeRef.current.src = `${IFRAME_URL}?template=template-1`;
        }
        setIsBoardLoading(false);
        setCreatingTemplateName('');
        alert(`Error creating board: ${data.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error creating template:', error);
      // Remove the temporary template on error
      setAvailableTemplates(prev => prev.filter(t => t.id !== tempTemplateId));
      // Switch back to template-1
      setSelectedTemplate('template-1');
      if (iframeRef.current) {
        iframeRef.current.src = `${IFRAME_URL}?template=template-1`;
      }
      setIsBoardLoading(false);
      setCreatingTemplateName('');
      alert(`Error creating board: ${error.message}`);
    } finally {
      setIsCreatingTemplate(false);
      setCreatingTemplateName('');
    }
  };

  // ── Auth gate ────────────────────────────────────────────────────────────
  if (authLoading) {
    return (
      <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100vh', fontFamily:'sans-serif', color:'#555' }}>
        Loading…
      </div>
    );
  }

  if (!isAuthenticated && process.env.REACT_APP_AUTH0_DOMAIN) {
    return (
      <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', height:'100vh', fontFamily:'sans-serif', gap:'16px' }}>
        <h1 style={{ fontSize:'24px', fontWeight:700 }}>byteBOARD</h1>
        <p style={{ color:'#666' }}>Sign in to continue</p>
        <button
          onClick={() => loginWithRedirect()}
          style={{ padding:'10px 28px', background:'#111', color:'#fff', border:'none', borderRadius:'8px', fontSize:'15px', cursor:'pointer' }}
        >
          Sign In
        </button>
      </div>
    );
  }
  // ─────────────────────────────────────────────────────────────────────────

  return (
    <div className="App">
    <div className={`layout-shell ${chatDocked && !galleryViewMode ? 'chat-docked' : ''} ${chatDocked && !galleryViewMode && chatCollapsed ? 'chat-collapsed' : ''}`}>
      <div className="left-rail">
        <aside className="side-rail">
          <div className="brand-mark mini">
            <img 
              src="/logo/zero2AI_logomain.svg" 
              alt="Zero2AI Logo" 
              className="brand-logo mini"
            />
          </div>
          <div className="rail-actions">
            {(isAdmin || isCreator || !process.env.REACT_APP_AUTH0_DOMAIN) && (
              <button className="rail-btn icon primary" onClick={() => setShowAddTemplateModal(true)} title="New Board">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <line x1="12" y1="5" x2="12" y2="19"></line>
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                </svg>
                <span className="sr-only">New Board</span>
              </button>
            )}
            <button 
              className={`rail-btn icon ghost ${galleryNudge ? 'nudge' : ''}`} 
              onClick={() => setGalleryViewMode(true)} 
              title="Gallery"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <rect x="3" y="3" width="7" height="7"></rect>
                <rect x="14" y="3" width="7" height="7"></rect>
                <rect x="14" y="14" width="7" height="7"></rect>
                <rect x="3" y="14" width="7" height="7"></rect>
              </svg>
              <span className="sr-only">Gallery</span>
              {galleryTooltip && <span className="rail-tooltip">Go to Gallery</span>}
            </button>
            <button 
              className={`rail-btn icon ghost ${chatNudge ? 'nudge' : ''}`} 
              onClick={() => {
                setChatDocked(true);
                setChatCollapsed(false);
                setChatMinimized(true);
              }} 
              title="Board Copilot"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
              </svg>
              <span className="sr-only">Open Board Copilot</span>
              {chatTooltip && <span className="rail-tooltip">Board Copilot</span>}
            </button>
            {isAdmin && process.env.REACT_APP_AUTH0_DOMAIN && (
              <button
                className="rail-btn icon ghost"
                onClick={() => { setShowAdminPanel(true); fetchAdminUsers(); }}
                title="Admin Panel"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                  <circle cx="9" cy="7" r="4"></circle>
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                  <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                </svg>
                <span className="sr-only">Admin Panel</span>
              </button>
            )}
          </div>
        </aside>
        {chatDocked && !galleryViewMode && (
          <aside className={`chat-sidebar ${chatCollapsed ? 'collapsed' : ''}`}>
            <div className="chat-sidebar-header">
              {!chatCollapsed && <span className="chat-sidebar-title">Board Copilot</span>}
              <div className="chat-sidebar-actions">
                <button
                  className="icon-btn small"
                  onClick={() => setChatCollapsed(!chatCollapsed)}
                  title={chatCollapsed ? 'Expand chat' : 'Collapse chat'}
                >
                  {chatCollapsed ? (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                      <polyline points="9 18 15 12 9 6"></polyline>
                    </svg>
                  ) : (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                      <polyline points="15 18 9 12 15 6"></polyline>
                    </svg>
                  )}
                  <span className="sr-only">{chatCollapsed ? 'Expand chat' : 'Collapse chat'}</span>
                </button>
                <button
                  className="icon-btn small"
                  onClick={() => {
                    setChatDocked(false);
                    setChatMinimized(false);
                  }}
                  title="Pop out chat"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                    <path d="M18 3h3v3"></path>
                    <path d="M21 3l-7 7"></path>
                    <rect x="3" y="7" width="14" height="14" rx="2" ry="2"></rect>
                  </svg>
                  <span className="sr-only">Pop out chat</span>
                </button>
              </div>
            </div>
            {!chatCollapsed && (
              <div className="chat-sidebar-body">
                <ChatAdvisor
                  key={`chat-${selectedTemplate}`}
                  selectedTemplate={selectedTemplate}
                  isOwner={templateOwnership[selectedTemplate]?.is_owner ?? true}
                  getAuthHeaders={getAuthHeaders}
                />
              </div>
            )}
          </aside>
        )}
      </div>
          <div className="main-pane">
          <div className="top-actions compact">
            <div className="top-actions-left"></div>
            <div className="top-brand-center">
              <div className="brand-mark mini center">
                <div className="brand-type">
                  <div className="app-logo-text brand-kicker">
                    <span className="logo-byte">byte</span>
                    <span className="logo-board">BOARD</span>
                  </div>
                  <div className="brand-tagline">AI digital whiteboards</div>
                </div>
              </div>
            </div>
            <div className="top-actions-right">
              {/* User info + logout (shown only when Auth0 is active) */}
              {isAuthenticated && process.env.REACT_APP_AUTH0_DOMAIN && (
                <div style={{ display:'flex', alignItems:'center', gap:'6px', marginRight:'8px', minWidth:0, flexShrink:0 }}>
                  {user?.picture ? (
                    <img
                      src={user.picture}
                      alt=""
                      style={{ width:26, height:26, borderRadius:'50%', objectFit:'cover', flexShrink:0 }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling && (e.target.nextSibling.style.display = 'flex');
                      }}
                    />
                  ) : null}
                  <span style={{
                    display: user?.picture ? 'none' : 'flex',
                    width:26, height:26, borderRadius:'50%', background:'#6d28d9',
                    color:'#fff', fontSize:'11px', fontWeight:700,
                    alignItems:'center', justifyContent:'center', flexShrink:0
                  }}>
                    {(user?.email || '?')[0].toUpperCase()}
                  </span>
                  <span style={{ fontSize:'11px', color:'#666', maxWidth:'90px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', flexShrink:1 }}
                    title={user?.email}>
                    {user?.email}
                  </span>
                  {effectiveRoles && (
                    <span style={{
                      fontSize:'10px', fontWeight:700, padding:'2px 7px', borderRadius:'99px',
                      flexShrink:0, letterSpacing:'0.04em', textTransform:'uppercase',
                      ...(isAdmin    ? { background:'#ede9fe', color:'#6d28d9' } :
                          isCreator  ? { background:'#dbeafe', color:'#1d4ed8' } :
                                       { background:'#dcfce7', color:'#15803d' })
                    }}>
                      {isAdmin ? 'Admin' : isCreator ? 'Creator' : 'User'}
                    </span>
                  )}
                  <button
                    onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
                    title="Sign out"
                    style={{ fontSize:'11px', padding:'3px 8px', background:'#f0f0f0', border:'1px solid #ddd', borderRadius:'5px', cursor:'pointer', flexShrink:0, whiteSpace:'nowrap' }}
                  >
                    Sign out
                  </button>
                </div>
              )}
              <span className="save-status">{saveStatus}</span>
            </div>
          </div>
          {isAuthenticated && process.env.REACT_APP_AUTH0_DOMAIN && effectiveRoles !== null && availableTemplates.length === 0 ? (
            <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', flex:1, gap:'16px', padding:'40px', textAlign:'center', color:'#555' }}>
              <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="#bbb" strokeWidth="1.5">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
              </svg>
              <div>
                <p style={{ fontSize:'17px', fontWeight:600, marginBottom:'8px', color:'#333' }}>No boards assigned yet</p>
                <p style={{ fontSize:'14px', lineHeight:'1.6', maxWidth:'360px', color:'#777' }}>
                  You don't have any boards assigned yet.<br/>
                  Please contact your admin to get access to boards or have a role assigned to you.
                </p>
              </div>
            </div>
          ) : galleryViewMode ? (
            <div className="inline-gallery">
              <div className="gallery-grid inline">
                {availableTemplates.map(template => (
                  <div 
                    key={template.id} 
                    className="gallery-item inline"
                    onClick={() => handleOpenTemplateFromGallery(template.id)}
                    title={`Open ${template.name}`}
                  >
                    <div className="gallery-card">
                      <div className="gallery-face front">
                        <div className="gallery-hover-anchor">
                          <div className="gallery-hover-bubble">
                            <p className="bubble-title">Inside This Board</p>
                            <p className="bubble-text">{template.description || `Interactive board for ${template.name}.`}</p>
                            <p className="bubble-text subtle">Click to open and edit.</p>
                            <span className="bubble-cta">Open board</span>
                          </div>
                        </div>
                        <div className="gallery-item-preview">
                          <iframe
                            src={`${IFRAME_URL}?template=${template.id}`}
                            className="gallery-preview-iframe"
                            title={`Preview of ${template.name}`}
                            loading="lazy"
                            allow="camera; microphone"
                            onLoad={(e) => {
                              e.target.classList.add('loaded');
                              const loadingDiv = e.target.nextElementSibling;
                              if (loadingDiv) {
                                loadingDiv.style.display = 'none';
                              }
                            }}
                          />
                          <div className="gallery-preview-loading">
                            <div className="gallery-loading-spinner"></div>
                          </div>
                        </div>
                        <div className="gallery-item-info">
                          <h3 className="gallery-item-name">{template.name}</h3>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
          <div className={`board-frame${isFullscreen ? ' fullscreen' : ''}`} ref={boardFrameRef}>
            {isBoardLoading && !boardError && (
              <div className="board-loading-overlay">
                <div className="board-loading-spinner">
                  <div className="spinner-ring"></div>
                  <div className="spinner-ring"></div>
                  <div className="spinner-ring"></div>
                  <div className="spinner-ring"></div>
                </div>
                <div className="board-loading-text">
                  {isCreatingTemplate && creatingTemplateName 
                    ? `Creating "${creatingTemplateName}"...` 
                    : 'Loading board...'}
                </div>
              </div>
            )}
            {boardError && (
              <div className="board-error-overlay">
                <div className="board-error-icon">!</div>
                <div className="board-error-title">Error Loading Board</div>
                <div className="board-error-message">{boardError}</div>
                <button 
                  className="board-error-retry"
                  onClick={() => {
                    setBoardError(null);
                    setIsBoardLoading(true);
                    if (iframeRef.current) {
                      const currentSrc = iframeRef.current.src;
                      iframeRef.current.src = '';
                      setTimeout(() => {
                        if (iframeRef.current) {
                          iframeRef.current.src = currentSrc;
                        }
                      }, 100);
                    }
                  }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                    <polyline points="23 4 23 10 17 10"></polyline>
                    <path d="M20.49 15a9 9 0 1 1 2.13-9"></path>
                  </svg>
                  <span className="sr-only">Retry</span>
                </button>
              </div>
            )}
            <div className="floating-board-actions">
              <button 
                className="icon-btn round"
                onClick={handleFullscreen}
                title={isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'}
              >
                {isFullscreen ? (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                    <polyline points="9 3 3 3 3 9"></polyline>
                    <polyline points="15 21 21 21 21 15"></polyline>
                    <line x1="3" y1="3" x2="10" y2="10"></line>
                    <line x1="21" y1="21" x2="14" y2="14"></line>
                  </svg>
                ) : (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                    <polyline points="15 3 21 3 21 9"></polyline>
                    <polyline points="9 21 3 21 3 15"></polyline>
                    <line x1="21" y1="3" x2="14" y2="10"></line>
                    <line x1="3" y1="21" x2="10" y2="14"></line>
                  </svg>
                )}
                <span className="sr-only">{isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'}</span>
              </button>
              {(isAdmin || isCreator) && (
                <>
                  <button
                    className="icon-btn round"
                    onClick={handleQuickUndo}
                    disabled={quickUndoLoading}
                    title={quickUndoLoading ? 'Restoring…' : 'Quick Undo (restore last version)'}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden="true" style={quickUndoLoading ? {opacity: 0.4} : {}}>
                      <polyline points="1 4 1 10 7 10"></polyline>
                      <path d="M3.51 15a9 9 0 1 0 .49-4.95L1 10"></path>
                    </svg>
                    <span className="sr-only">Quick Undo</span>
                  </button>
                  <button
                    className="icon-btn round"
                    onClick={handleOpenVersionModal}
                    title="Version History"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                      <circle cx="12" cy="12" r="10"></circle>
                      <polyline points="12 6 12 12 16 14"></polyline>
                    </svg>
                    <span className="sr-only">Version History</span>
                  </button>
                </>
              )}
              {templateOwnership[selectedTemplate]?.is_owner && !galleryViewMode && !isUser && (
                <button
                  className="icon-btn round"
                  onClick={handleDeployClick}
                  disabled={isDeploying}
                  title={existingDeployment ? 'Redeploy to Cloudflare Pages' : 'Deploy to Cloudflare Pages'}
                >
                  {isDeploying ? (
                    <div className="deploy-spinner" style={{width:14,height:14}}></div>
                  ) : (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                      <polyline points="17 8 12 3 7 8"></polyline>
                      <line x1="12" y1="3" x2="12" y2="15"></line>
                    </svg>
                  )}
                  <span className="sr-only">{existingDeployment ? 'Redeploy' : 'Deploy'}</span>
                </button>
              )}
              {templateOwnership[selectedTemplate]?.is_owner && !galleryViewMode && (
                <button
                  className="icon-btn round"
                  onClick={() => setShowCodePreview(true)}
                  title="View and edit code"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                    <polyline points="16 18 22 12 16 6"></polyline>
                    <polyline points="8 6 2 12 8 18"></polyline>
                  </svg>
                  <span className="sr-only">Code editor</span>
                </button>
              )}
              {selectedTemplate && !templateOwnership[selectedTemplate]?.is_owner && selectedTemplate !== 'template-1' && !isUser && (
                <button
                  className="icon-btn round"
                  onClick={() => {
                    const templateName = availableTemplates.find(t => t.id === selectedTemplate)?.name || 'Board';
                    setCopyAppName(`${templateName} (Copy)`);
                    setShowCopyModal(true);
                  }}
                  title="Copy this board"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                  </svg>
                  <span className="sr-only">Copy board</span>
                </button>
              )}
              {selectedTemplate !== 'template-1' && templateOwnership[selectedTemplate]?.is_owner && !galleryViewMode && !isUser && (
                <button
                  className="icon-btn round danger"
                  onClick={() => handleDeleteClick(selectedTemplate)}
                  title={`Delete ${availableTemplates.find(t => t.id === selectedTemplate)?.name || selectedTemplate}`}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                    <polyline points="3 6 5 6 21 6"></polyline>
                    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"></path>
                    <path d="M10 11v6"></path>
                    <path d="M14 11v6"></path>
                    <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"></path>
                  </svg>
                  <span className="sr-only">Delete board</span>
                </button>
              )}
            </div>
            <iframe
              ref={iframeRef}
              title="ByteBOARD Canvas"
              src={`${IFRAME_URL}?template=${selectedTemplate}`}
              className="board-iframe"
              key={selectedTemplate} // Force reload when template changes
              allow="camera; microphone"
            />
              <BoardIframe
                iframeRef={iframeRef}
                setIsBoardLoading={setIsBoardLoading}
                selectedTemplate={selectedTemplate}
                getAuthHeaders={getAuthHeaders}
              />
          </div>
          )}
        </div>
      </div>
      
      {/* Add Template Modal */}
      {showAddTemplateModal && (
        <div className="modal-overlay" onClick={() => {
          if (!isCreatingTemplate) {
            setShowAddTemplateModal(false);
            setNewTemplateModel('openai');
            setCrossAppEnabled(false); setCrossAppReadFrom([]); setCrossAppWriteTo([]);
          }
        }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Create New Board</h2>
              <button 
                className="modal-close"
                onClick={() => {
                  if (!isCreatingTemplate) {
                    setShowAddTemplateModal(false);
                    setNewTemplateModel('openai');
                    setCrossAppEnabled(false); setCrossAppReadFrom([]); setCrossAppWriteTo([]);
                  }
                }}
                disabled={isCreatingTemplate}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
                <span className="sr-only">Close</span>
              </button>
            </div>
            <div className="modal-body">
              {isCreatingTemplate ? (
                <div className="template-creating">
                  <div className="creating-spinner">
                    <div className="spinner-ring"></div>
                    <div className="spinner-ring"></div>
                    <div className="spinner-ring"></div>
                    <div className="spinner-ring"></div>
                  </div>
                  <h3>Creating Board...</h3>
                  <p>AI is generating your board based on your description. This may take a moment.</p>
                  <div className="creating-steps">
                    <div className="step-item">
                      <span className="step-icon">*</span>
                      <span>Analyzing your requirements</span>
                    </div>
                    <div className="step-item">
                      <span className="step-icon">*</span>
                      <span>Generating HTML code</span>
                    </div>
                    <div className="step-item">
                      <span className="step-icon">*</span>
                      <span>Creating board file</span>
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  <div className="form-group">
                    <label htmlFor="template-name">Board Name:</label>
                    <input
                      id="template-name"
                      type="text"
                      value={newTemplateName}
                      onChange={(e) => setNewTemplateName(e.target.value)}
                      placeholder="e.g., Project Dashboard"
                      disabled={isCreatingTemplate}
                      className="form-input"
                    />
                  </div>
                  <div className="form-group">
                  <label htmlFor="template-description">Board Description / Prompt:</label>
                  <textarea
                    id="template-description"
                    value={newTemplateDescription}
                    onChange={(e) => setNewTemplateDescription(e.target.value)}
                    placeholder="Describe the board you want to create, the layout, and key widgets or sections."
                    disabled={isCreatingTemplate}
                    className="form-textarea"
                    rows={5}
                  />
                  </div>
                  <div className="form-group">
                    <label htmlFor="template-model">AI Model:</label>
                    <select
                      id="template-model"
                      value={newTemplateModel}
                      onChange={(e) => setNewTemplateModel(e.target.value)}
                      disabled={isCreatingTemplate}
                      className="form-input"
                      style={{ 
                        padding: '8px 12px',
                        borderRadius: '6px',
                        border: '1px solid var(--border)',
                        backgroundColor: 'var(--panel-bg)',
                        color: 'var(--text-primary)',
                        fontSize: '14px',
                        cursor: isCreatingTemplate ? 'not-allowed' : 'pointer',
                        outline: 'none',
                        transition: 'border-color 0.2s'
                      }}
                    >
                      <option value="openai">GPT 4.1</option>
                      <option value="gemini">Google Gemini 3 Pro</option>
                      <option value="gpt5">GPT 5.1</option>
                    </select>
                    <small style={{ color: '#666', marginTop: '4px', display: 'block' }}>
                      Select which AI model to use for generating your board
                    </small>
                  </div>
                  <div className="form-group">
                    <label htmlFor="template-image">Reference Image (Optional):</label>
                    <input
                      type="file"
                      id="template-image"
                      ref={templateImageInputRef}
                      accept="image/*"
                      onChange={handleTemplateImageSelect}
                      disabled={isCreatingTemplate}
                      style={{ display: 'none' }}
                    />
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                      <button
                        type="button"
                        onClick={() => templateImageInputRef.current?.click()}
                        className="image-upload-button"
                        disabled={isCreatingTemplate}
                        style={{ padding: '8px 16px', cursor: 'pointer' }}
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                          <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                          <circle cx="8.5" cy="8.5" r="1.5"></circle>
                          <polyline points="21 15 16 10 5 21"></polyline>
                        </svg>
                        <span className="sr-only">Choose image</span>
                      </button>
                      {newTemplateImagePreview && (
                        <>
                          <img 
                            src={newTemplateImagePreview} 
                            alt="Preview" 
                            style={{ maxWidth: '100px', maxHeight: '100px', borderRadius: '4px' }}
                          />
                          <button
                            type="button"
                            onClick={removeTemplateImage}
                            disabled={isCreatingTemplate}
                            style={{ padding: '4px 8px', cursor: 'pointer' }}
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                              <line x1="18" y1="6" x2="6" y2="18"></line>
                              <line x1="6" y1="6" x2="18" y2="18"></line>
                            </svg>
                            <span className="sr-only">Remove image</span>
                          </button>
                        </>
                      )}
                    </div>
                    <small style={{ color: '#666', marginTop: '4px', display: 'block' }}>
                      Upload an image to use as design inspiration for the template
                    </small>
                  </div>
                  <div className="form-group">
                    <label htmlFor="template-file">Data File (CSV/Excel) - Optional:</label>
                    <input
                      type="file"
                      id="template-file"
                      ref={templateFileInputRef}
                      accept=".csv,.xlsx,.xls"
                      onChange={handleTemplateFileSelect}
                      disabled={isCreatingTemplate}
                      style={{ display: 'none' }}
                    />
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                      <button
                        type="button"
                        onClick={() => templateFileInputRef.current?.click()}
                        className="image-upload-button"
                        disabled={isCreatingTemplate}
                        style={{ padding: '8px 16px', cursor: 'pointer' }}
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                          <polyline points="14 2 14 8 20 8"></polyline>
                          <line x1="8" y1="13" x2="16" y2="13"></line>
                          <line x1="8" y1="17" x2="16" y2="17"></line>
                        </svg>
                        <span className="sr-only">Choose data file</span>
                      </button>
                      {newTemplateFile && (
                        <>
                          <span style={{ fontSize: '13px', color: 'var(--text-primary)' }}>
                             {newTemplateFile.name}
                          </span>
                          <button
                            type="button"
                            onClick={removeTemplateFile}
                            disabled={isCreatingTemplate}
                            style={{ padding: '4px 8px', cursor: 'pointer' }}
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                              <line x1="18" y1="6" x2="6" y2="18"></line>
                              <line x1="6" y1="6" x2="18" y2="18"></line>
                            </svg>
                            <span className="sr-only">Remove file</span>
                          </button>
                        </>
                      )}
                    </div>
                    <small style={{ color: '#666', marginTop: '4px', display: 'block' }}>
                      Upload a CSV or Excel file to incorporate data into the template
                    </small>
                  </div>
                  {/* Cross-App Data Access */}
                  <div className="form-group">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                      <input
                        type="checkbox"
                        id="cross-app-toggle"
                        checked={crossAppEnabled}
                        onChange={e => {
                          setCrossAppEnabled(e.target.checked);
                          if (!e.target.checked) { setCrossAppReadFrom([]); setCrossAppWriteTo([]); }
                        }}
                        disabled={isCreatingTemplate}
                        style={{ cursor: 'pointer' }}
                      />
                      <label htmlFor="cross-app-toggle" style={{ cursor: 'pointer', fontWeight: 500 }}>
                        Connect to data from other boards
                      </label>
                    </div>
                    {crossAppEnabled && (
                      <div style={{ border: '1px solid var(--border)', borderRadius: '6px', padding: '10px', marginTop: '6px' }}>
                        {availableTemplates.filter(t => t.app_uuid).length === 0 ? (
                          <small style={{ color: '#888' }}>No other boards with data storage found.</small>
                        ) : (
                          <>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '6px 12px', alignItems: 'center', marginBottom: '4px' }}>
                              <span style={{ fontSize: '12px', color: '#888', fontWeight: 600 }}>Board</span>
                              <span style={{ fontSize: '12px', color: '#888', fontWeight: 600 }}>Read</span>
                              <span style={{ fontSize: '12px', color: '#888', fontWeight: 600 }}>Write</span>
                            </div>
                            {availableTemplates.filter(t => t.app_uuid).map(t => (
                              <div key={t.id} style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '6px 12px', alignItems: 'center', padding: '3px 0' }}>
                                <div>
                                  <div style={{ fontSize: '13px', color: 'var(--text-primary)' }}>{t.name}</div>
                                  <div style={{ fontSize: '10px', color: '#888', fontFamily: 'monospace' }}>{t.app_uuid}</div>
                                </div>
                                <input
                                  type="checkbox"
                                  checked={crossAppReadFrom.includes(t.app_uuid)}
                                  onChange={e => setCrossAppReadFrom(prev =>
                                    e.target.checked ? [...prev, t.app_uuid] : prev.filter(u => u !== t.app_uuid)
                                  )}
                                  disabled={isCreatingTemplate}
                                  style={{ cursor: 'pointer' }}
                                  title={`Allow this new board to read data from "${t.name}"`}
                                />
                                <input
                                  type="checkbox"
                                  checked={crossAppWriteTo.includes(t.app_uuid)}
                                  onChange={e => setCrossAppWriteTo(prev =>
                                    e.target.checked ? [...prev, t.app_uuid] : prev.filter(u => u !== t.app_uuid)
                                  )}
                                  disabled={isCreatingTemplate}
                                  style={{ cursor: 'pointer' }}
                                  title={`Allow this new board to write data to "${t.name}"`}
                                />
                              </div>
                            ))}
                          </>
                        )}
                      </div>
                    )}
                    <small style={{ color: '#666', marginTop: '4px', display: 'block' }}>
                      Allow this board to read or write data from other boards via ByteboardDB
                    </small>
                  </div>
                  <div className="modal-footer">
                    <button
                      className="modal-button cancel"
                      onClick={() => {
                        setShowAddTemplateModal(false);
                        setNewTemplateModel('openai');
                        setCrossAppEnabled(false); setCrossAppReadFrom([]); setCrossAppWriteTo([]);
                      }}
                      disabled={isCreatingTemplate}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                        <line x1="18" y1="6" x2="6" y2="18"></line>
                        <line x1="6" y1="6" x2="18" y2="18"></line>
                      </svg>
                      <span className="sr-only">Cancel</span>
                    </button>
                    <button
                      className="modal-button primary"
                      onClick={handleCreateTemplate}
                      disabled={isCreatingTemplate || !newTemplateName.trim() || !newTemplateDescription.trim()}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                        <line x1="12" y1="5" x2="12" y2="19"></line>
                        <line x1="5" y1="12" x2="19" y2="12"></line>
                      </svg>
                      <span className="sr-only">Create board</span>
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Version History Modal */}
      {showVersionModal && (
        <div className="modal-overlay" onClick={() => setShowVersionModal(false)}>
          <div className="modal-content version-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Version History</h2>
              <button className="modal-close" onClick={() => setShowVersionModal(false)}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
                <span className="sr-only">Close</span>
              </button>
            </div>
            <div className="modal-body">
              {versionsLoading ? (
                <p className="version-loading">Loading versions…</p>
              ) : versions.length === 0 ? (
                <p className="version-empty">No versions yet. Snapshots are saved automatically before every edit.</p>
              ) : (
                <ul className="version-list">
                  {versions.map((v, i) => {
                    // saved_at is "2024-03-24T10:23:45" (UTC, no Z suffix)
                    const date = new Date(v.saved_at + 'Z');
                    const dateLabel = date.toLocaleString(undefined, {
                      month: 'short', day: 'numeric',
                      hour: '2-digit', minute: '2-digit',
                    });
                    const sizeKb = (v.size / 1024).toFixed(1);
                    const sourceEmoji =
                      v.source === 'ai_edit'     ? '🤖 Copilot' :
                      v.source === 'code_editor' ? '✏️ Manual edit' :
                      v.label || v.source;
                    return (
                      <li key={v.version_id} className="version-item">
                        <div className="version-info">
                          {i === 0 && <span className="version-latest-badge">Latest</span>}
                          <span className="version-source">{sourceEmoji}</span>
                          <span className="version-meta">{dateLabel} · {sizeKb} KB</span>
                        </div>
                        <button
                          className="version-restore-btn"
                          disabled={revertingVersion === v.version_id}
                          onClick={() => handleRevert(v.version_id)}
                        >
                          {revertingVersion === v.version_id ? 'Restoring…' : 'Restore'}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Delete Template Confirmation Modal */}
      {showDeleteModal && templateToDelete && (
        <div className="modal-overlay" onClick={handleDeleteCancel}>
          <div className="modal-content delete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Delete Board</h2>
              <button 
                className="modal-close"
                onClick={handleDeleteCancel}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
                <span className="sr-only">Close</span>
              </button>
            </div>
            <div className="modal-body">
              {deleteStep === 1 ? (
                <>
                  <div className="delete-warning">
                    <div className="warning-icon">!</div>
                    <h3>Are you sure?</h3>
                    <p>You are about to delete <strong>"{templateToDelete.name}"</strong>.</p>
                    <p>This action cannot be undone.</p>
                  </div>
                  <div className="modal-footer">
                    <button
                      className="modal-button cancel"
                      onClick={handleDeleteCancel}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                        <line x1="18" y1="6" x2="6" y2="18"></line>
                        <line x1="6" y1="6" x2="18" y2="18"></line>
                      </svg>
                      <span className="sr-only">Cancel</span>
                    </button>
                    <button
                      className="modal-button danger"
                      onClick={handleDeleteConfirm}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                        <polyline points="3 6 5 6 21 6"></polyline>
                        <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"></path>
                        <path d="M10 11v6"></path>
                        <path d="M14 11v6"></path>
                        <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"></path>
                      </svg>
                      <span className="sr-only">Delete</span>
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="delete-warning step2">
                    <div className="warning-icon">!</div>
                    <h3>Final Confirmation</h3>
                    <p>This is your last chance to cancel.</p>
                    <p>Deleting <strong>"{templateToDelete.name}"</strong> will permanently remove:</p>
                    <ul className="delete-consequences">
                      <li>The board file</li>
                      <li>All associated chat history</li>
                      <li>All saved data for this board</li>
                    </ul>
                    <p className="final-warning">Are you absolutely sure?</p>
                  </div>
                  <div className="modal-footer">
                    <button
                      className="modal-button cancel"
                      onClick={handleDeleteCancel}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                        <line x1="18" y1="6" x2="6" y2="18"></line>
                        <line x1="6" y1="6" x2="18" y2="18"></line>
                      </svg>
                      <span className="sr-only">Cancel</span>
                    </button>
                    <button
                      className="modal-button danger"
                      onClick={handleDeleteConfirm}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                        <polyline points="3 6 5 6 21 6"></polyline>
                        <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"></path>
                        <path d="M10 11v6"></path>
                        <path d="M14 11v6"></path>
                        <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"></path>
                      </svg>
                      <span className="sr-only">Delete forever</span>
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Gallery Modal - opened from menu dropdown */}
      {showGallery && (
        <div className="modal-overlay" onClick={() => setShowGallery(false)}>
          <div className="modal-content gallery-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Boards (Gallery)</h2>
              <button 
                className="modal-close"
                onClick={() => setShowGallery(false)}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
                <span className="sr-only">Close</span>
              </button>
            </div>
            <div className="modal-body gallery-body">
              <p className="gallery-description">Select a board to open it in edit view.</p>
              <div className="gallery-grid">
                {availableTemplates.map(template => (
                  <div 
                    key={template.id} 
                    className="gallery-item"
                    onClick={() => handleOpenTemplateFromGallery(template.id)}
                  >
                    <div className="gallery-item-preview">
                      <iframe
                        src={`${IFRAME_URL}?template=${template.id}`}
                        className="gallery-preview-iframe"
                        title={`Preview of ${template.name}`}
                        loading="lazy"
                        onLoad={(e) => {
                          e.target.classList.add('loaded');
                          const loadingDiv = e.target.nextElementSibling;
                          if (loadingDiv) {
                            loadingDiv.style.display = 'none';
                          }
                        }}
                      />
                      <div className="gallery-preview-loading">
                        <div className="gallery-loading-spinner"></div>
                      </div>
                    </div>
                    <div className="gallery-item-info">
                      <h3 className="gallery-item-name">{template.name}</h3>
                      <p className="gallery-item-id">{template.id}</p>
                    </div>
                    <div className="gallery-item-overlay">
                      <span className="gallery-item-action">Open</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Code Preview Modal */}
      {showCodePreview && (
        <CodePreview
          templateId={selectedTemplate}
          isOwner={templateOwnership[selectedTemplate]?.is_owner ?? true}
          onClose={() => setShowCodePreview(false)}
        />
      )}

      {/* Copy Board Modal */}
      {showCopyModal && (
        <div className="modal-overlay" onClick={() => {
          if (!isCopying) {
            setShowCopyModal(false);
            setCopyAppName('');
          }
        }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Copy Board</h2>
              <button 
                className="modal-close"
                onClick={() => {
                  if (!isCopying) {
                    setShowCopyModal(false);
                    setCopyAppName('');
                  }
                }}
                disabled={isCopying}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
                <span className="sr-only">Close</span>
              </button>
            </div>
            <div className="modal-body">
              <p style={{ marginBottom: '16px', color: 'var(--text-primary)' }}>
                Create a copy of this board that you can edit. The original board will remain unchanged.
              </p>
              <div className="form-group">
                <label htmlFor="copy-app-name">New Board Name:</label>
                <input
                  id="copy-app-name"
                  type="text"
                  value={copyAppName}
                  onChange={(e) => setCopyAppName(e.target.value)}
                  placeholder="Enter a name for the copied board"
                  disabled={isCopying}
                  className="form-input"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !isCopying && copyAppName.trim()) {
                      handleCopyApp();
                    }
                  }}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="modal-button cancel"
                onClick={() => {
                  setShowCopyModal(false);
                  setCopyAppName('');
                }}
                disabled={isCopying}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
                <span className="sr-only">Cancel</span>
              </button>
              <button
                className="modal-button primary"
                onClick={handleCopyApp}
                disabled={isCopying || !copyAppName.trim()}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                  <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                </svg>
                <span className="sr-only">{isCopying ? 'Copying' : 'Copy board'}</span>
              </button>
            </div>
          </div>
        </div>
      )}


      {showDeployConfirmModal && (
        <div className="modal-overlay" onClick={() => setShowDeployConfirmModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Deploy to Cloudflare</h2>
              <button
                className="modal-close"
                onClick={() => setShowDeployConfirmModal(false)}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
                <span className="sr-only">Close</span>
              </button>
            </div>
            <div className="modal-body">
              <p className="deployment-note">
                This will publish the current board to Cloudflare Pages and generate a shareable link.
              </p>
              {existingDeployment?.deployment_url && (
                <div className="deployment-existing">
                  <span>Last deployed:</span>
                  <a href={existingDeployment.deployment_url} target="_blank" rel="noreferrer">
                    {existingDeployment.deployment_url}
                  </a>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="modal-button cancel" onClick={() => setShowDeployConfirmModal(false)}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
                <span className="sr-only">Cancel</span>
              </button>
              <button className="modal-button primary" onClick={handleDeployConfirm}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                  <polyline points="17 8 12 3 7 8"></polyline>
                  <line x1="12" y1="3" x2="12" y2="15"></line>
                </svg>
                <span className="sr-only">Deploy</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {showDeployModal && (
        <div className="modal-overlay" onClick={() => { if (!isDeploying) setShowDeployModal(false); }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Deployment Status</h2>
              <button
                className="modal-close"
                onClick={() => { if (!isDeploying) setShowDeployModal(false); }}
                disabled={isDeploying}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
                <span className="sr-only">Close</span>
              </button>
            </div>
            <div className="modal-body">
              {isDeploying ? (
                <div className="deployment-loading">
                  <div className="deploy-spinner large"></div>
                  <p>Deploying to Cloudflare Pages...</p>
                </div>
              ) : deploymentStatus === 'success' && deploymentUrl ? (
                <div className="deployment-success">
                  <h3>Deployment successful</h3>
                  <p>Your board is live:</p>
                  <a href={deploymentUrl} target="_blank" rel="noreferrer">{deploymentUrl}</a>
                </div>
              ) : deploymentStatus === 'error' ? (
                <div className="deployment-error">
                  <h3>Deployment failed</h3>
                  <p>Please try again or check the server logs.</p>
                </div>
              ) : null}
            </div>
            {!isDeploying && (
              <div className="modal-footer">
                <button className="modal-button cancel" onClick={() => setShowDeployModal(false)}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                  <span className="sr-only">Close</span>
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Admin Panel Modal ─────────────────────────────────────────────── */}
      {showAdminPanel && (() => {
        const ROLE_META = {
          admin:   { label: 'Admin',   color: '#6d28d9', bg: '#ede9fe', desc: 'Full access. Can manage boards, users, and deployments.' },
          creator: { label: 'Creator', color: '#1d4ed8', bg: '#dbeafe', desc: 'Can create new boards and edit/deploy boards assigned to them. Cannot manage users.' },
          user:    { label: 'User',    color: '#15803d', bg: '#dcfce7', desc: 'Limited access. Can only view boards assigned by an admin.' },
        };
        const grouped = { admin: [], creator: [], user: [], none: [] };
        adminUsers.forEach(u => {
          if (u.roles.includes('admin'))         grouped.admin.push(u);
          else if (u.roles.includes('creator'))  grouped.creator.push(u);
          else if (u.roles.includes('user'))     grouped.user.push(u);
          else                                   grouped.none.push(u);
        });
        const userRoleUsers = [...grouped.creator, ...grouped.user];

        const renderAvatar = (u) => u.picture
          ? <img
              src={u.picture}
              alt=""
              className="admin-avatar-img"
              onError={(e) => {
                e.target.style.display = 'none';
                const placeholder = document.createElement('div');
                placeholder.className = 'admin-avatar-placeholder';
                placeholder.textContent = (u.name || u.email || '?')[0].toUpperCase();
                e.target.parentNode.appendChild(placeholder);
              }}
            />
          : <div className="admin-avatar-placeholder">{(u.name || u.email || '?')[0].toUpperCase()}</div>;

        return (
          <div className="modal-overlay" onClick={() => setShowAdminPanel(false)}>
            <div className="modal-content admin-panel-modal" onClick={e => e.stopPropagation()}>
              {/* Header */}
              <div className="modal-header admin-panel-header">
                <div className="admin-header-left">
                  <h2>Admin Panel</h2>
                  <div className="admin-tabs">
                    <button className={`admin-tab${adminTab === 'users' ? ' active' : ''}`} onClick={() => setAdminTab('users')}>
                      Users
                    </button>
                    <button className={`admin-tab${adminTab === 'access' ? ' active' : ''}`} onClick={() => setAdminTab('access')}>
                      Board Access
                    </button>
                  </div>
                </div>
                <button className="modal-close" onClick={() => setShowAdminPanel(false)}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                  <span className="sr-only">Close</span>
                </button>
              </div>

              {/* Body */}
              <div className="modal-body admin-panel-body">
                {adminUsersLoading ? (
                  <div className="admin-loading">
                    <div className="gallery-loading-spinner"></div>
                    <p>Loading users…</p>
                  </div>
                ) : adminTab === 'users' ? (
                  /* ── Users Tab ── */
                  <div className="admin-users-tab">
                    {/* Role cards */}
                    <div className="admin-role-cards">
                      {Object.entries(ROLE_META).map(([roleKey, meta]) => (
                        <div key={roleKey} className="admin-role-card" style={{ borderTopColor: meta.color }}>
                          <div className="admin-role-card-header">
                            <div className="admin-role-icon" style={{ background: meta.bg, color: meta.color }}>
                              {roleKey === 'admin' ? (
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
                              ) : roleKey === 'creator' ? (
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/></svg>
                              ) : (
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                              )}
                            </div>
                            <span className="admin-role-card-name">{meta.label}</span>
                            <span className="admin-role-badge" style={{ background: meta.bg, color: meta.color }}>{roleKey}</span>
                          </div>
                          <p className="admin-role-card-desc">{meta.desc}</p>
                          <div className="admin-role-card-count" style={{ color: meta.color }}>
                            {grouped[roleKey].length} {grouped[roleKey].length === 1 ? 'user' : 'users'}
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Users grouped by role */}
                    {adminUsers.length === 0 ? (
                      <div className="admin-empty">
                        <p>No users yet. Users appear here after their first login.</p>
                      </div>
                    ) : (
                      ['admin', 'creator', 'user', 'none'].map(roleKey => {
                        const group = grouped[roleKey];
                        if (group.length === 0) return null;
                        const meta = ROLE_META[roleKey] || { label: 'No Role', color: '#94a3b8', bg: '#f1f5f9' };
                        return (
                          <div key={roleKey} className="admin-group">
                            <div className="admin-group-header" style={{ borderLeftColor: meta.color }}>
                              <span className="admin-group-label" style={{ color: meta.color }}>{meta.label}</span>
                              <span className="admin-group-count">{group.length}</span>
                            </div>
                            <div className="admin-group-users">
                              {group.map(u => {
                                const currentRole = adminRoleEdits[u.user_id] ?? (u.roles[0] || '');
                                const isSelf = user?.sub === u.user_id;
                                return (
                                  <div key={u.user_id} className="admin-user-card">
                                    <div className="admin-user-avatar">{renderAvatar(u)}</div>
                                    <div className="admin-user-info">
                                      <span className="admin-user-name">
                                        {u.name || u.email}
                                        {isSelf && <span className="admin-self-badge">you</span>}
                                      </span>
                                      {u.name && u.email && <span className="admin-user-email">{u.email}</span>}
                                    </div>
                                    <div className="admin-role-edit">
                                      <select
                                        className="admin-role-select"
                                        value={currentRole}
                                        disabled={isSelf || adminSavingRole === u.user_id}
                                        onChange={e => setAdminRoleEdits(prev => ({ ...prev, [u.user_id]: e.target.value }))}
                                      >
                                        <option value="admin">Admin</option>
                                        <option value="creator">Creator</option>
                                        <option value="user">User</option>
                                      </select>
                                      <button
                                        className="admin-role-save-btn"
                                        disabled={isSelf || adminSavingRole === u.user_id || currentRole === (u.roles[0] || '')}
                                        onClick={() => saveUserRole(u.user_id)}
                                        title={isSelf ? "Cannot change your own role" : "Save role"}
                                      >
                                        {adminSavingRole === u.user_id ? '…' : 'Save'}
                                      </button>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                ) : (
                  /* ── Board Access Tab ── */
                  <div className="admin-access-tab">
                    {/* Left: board list */}
                    <div className="admin-access-boards">
                      <p className="admin-access-section-label">Select a board</p>
                      <ul className="admin-board-select-list">
                        {availableTemplates.map(t => (
                          <li
                            key={t.id}
                            className={`admin-board-select-item${adminSelectedBoard === t.id ? ' selected' : ''}`}
                            onClick={() => setAdminSelectedBoard(t.id)}
                          >
                            <span className="admin-board-select-name">{t.name}</span>
                            <span className="admin-board-select-id">{t.id}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Right: user assignment */}
                    <div className="admin-access-panel">
                      {!adminSelectedBoard ? (
                        <div className="admin-access-placeholder">
                          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="1.5"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>
                          <p>Select a board to manage user access</p>
                        </div>
                      ) : (
                        <>
                          <div className="admin-access-panel-title">
                            <strong>{availableTemplates.find(t => t.id === adminSelectedBoard)?.name || adminSelectedBoard}</strong>
                            <span className="admin-board-select-id">{adminSelectedBoard}</span>
                          </div>
                          <p className="admin-access-section-label" style={{ marginTop: 12 }}>
                            <span className="admin-role-badge" style={{ background: '#dbeafe', color: '#1d4ed8' }}>Creator</span>
                            &nbsp;&amp;&nbsp;
                            <span className="admin-role-badge" style={{ background: '#dcfce7', color: '#15803d' }}>User</span>
                            &nbsp;— assign board access
                          </p>
                          {userRoleUsers.length === 0 ? (
                            <p className="admin-empty-inline">No creators or users yet.</p>
                          ) : (
                            <ul className="admin-assign-user-list">
                              {userRoleUsers.map(u => {
                                const checked = (adminUserApps[u.user_id] || []).includes(adminSelectedBoard);
                                return (
                                  <li key={u.user_id} className="admin-assign-user-item">
                                    <label>
                                      <input
                                        type="checkbox"
                                        checked={checked}
                                        onChange={() => {
                                          const current = adminUserApps[u.user_id] || [];
                                          const updated = checked
                                            ? current.filter(id => id !== adminSelectedBoard)
                                            : [...current, adminSelectedBoard];
                                          setAdminUserApps(prev => ({ ...prev, [u.user_id]: updated }));
                                        }}
                                      />
                                      <div className="admin-user-avatar">{renderAvatar(u)}</div>
                                      <div className="admin-user-info">
                                        <span className="admin-user-name">{u.name || u.email}</span>
                                        {u.name && u.email && <span className="admin-user-email">{u.email}</span>}
                                      </div>
                                      {u.roles.includes('creator') && (
                                        <span className="admin-role-badge" style={{ background: '#dbeafe', color: '#1d4ed8', marginLeft: 'auto' }}>Creator</span>
                                      )}
                                    </label>
                                  </li>
                                );
                              })}
                            </ul>
                          )}
                          {userRoleUsers.length > 0 && (
                            <div className="admin-access-save-row">
                              <button
                                className="admin-save-btn"
                                disabled={!!adminSavingUser}
                                onClick={async () => {
                                  for (const u of userRoleUsers) {
                                    await saveUserApps(u.user_id);
                                  }
                                }}
                              >
                                {adminSavingUser ? 'Saving…' : 'Save Assignments'}
                              </button>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {!isFullscreen && !chatMinimized && !chatDocked && !galleryViewMode && (
        <div
          className={`floating-chat ${chatMinimized ? 'minimized' : ''}`}
          style={{ left: 0, top: 0, transform: `translate(${chatPosition.x}px, ${chatPosition.y}px)` }}
        >
          <div
            className="floating-chat-header"
            onMouseDown={(e) => {
              chatDragRef.current.dragging = true;
              chatDragRef.current.offsetX = chatPosition.x - e.clientX;
              chatDragRef.current.offsetY = chatPosition.y - e.clientY;
            }}
          >
            <span className="floating-chat-title">Board Copilot</span>
            <div className="floating-chat-actions">
              <button
                className="icon-btn small"
                onClick={() => setChatMinimized(!chatMinimized)}
                title={chatMinimized ? 'Expand chat' : 'Minimize chat'}
              >
                {chatMinimized ? (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                    <polyline points="6 9 12 15 18 9"></polyline>
                  </svg>
                ) : (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                    <line x1="5" y1="12" x2="19" y2="12"></line>
                  </svg>
                )}
                <span className="sr-only">{chatMinimized ? 'Expand chat' : 'Minimize chat'}</span>
              </button>
              <button
                className="icon-btn small"
                onClick={() => {
                  setChatDocked(true);
                  setChatCollapsed(false);
                  setChatMinimized(true);
                }}
                title="Dock chat"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <rect x="3" y="3" width="18" height="18" rx="2"></rect>
                  <line x1="9" y1="3" x2="9" y2="21"></line>
                </svg>
                <span className="sr-only">Dock chat</span>
              </button>
            </div>
          </div>
          {!chatMinimized && (
            <div className="floating-chat-body">
              <div className="floating-chat-content">
                <ChatAdvisor
                  key={`chat-${selectedTemplate}`}
                  selectedTemplate={selectedTemplate}
                  isOwner={templateOwnership[selectedTemplate]?.is_owner ?? true}
                  getAuthHeaders={getAuthHeaders}
                />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default App;




